import { Link, useOutletContext } from '@remix-run/react';
import { Loader2 } from 'lucide-react';
import type { CollectionsQuery } from '~/generated/graphql';
import { OutletContext } from '~/types';

type VariantType =
  CollectionsQuery['collections']['items'][number]['productVariants']['items'][number];

interface BentoGridProps {
  variants: VariantType[];
  collectionName: string;
}

export function BentoGrid({ variants }: BentoGridProps) {
  const { activeOrderFetcher } = useOutletContext<OutletContext>();
  const idBeingAddedToOrder = activeOrderFetcher?.formData?.get('variantId');

  const [largeVariant, mediumVariant, ...smallVariants] = variants;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12 lg:max-w-7xl lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Large Item */}
        {largeVariant && (
          <div className="group relative col-span-1 lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden p-4 flex flex-col items-center">
            <div className="w-full h-80 overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
              <img
                src={
                  largeVariant.featuredAsset?.preview ||
                  largeVariant.product.featuredAsset?.preview
                }
                alt={largeVariant.product.name}
                className="max-h-full max-w-full object-contain object-center p-4"
              />
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                <Link to={`/products/${largeVariant.product.slug}`}>
                  {largeVariant.product.name}
                </Link>
              </h3>
              <p className="text-xl font-bold text-gray-900 mt-2">
                {largeVariant.priceWithTax.toFixed(2)}{' '}
                {largeVariant.currencyCode}
              </p>

              <activeOrderFetcher.Form
                method="post"
                action="/api/active-order"
                className="mt-4"
              >
                <input type="hidden" name="variantId" value={largeVariant.id} />
                <input type="hidden" name="action" value="addItemToOrder" />
                <button
                  type="submit"
                  disabled={activeOrderFetcher.state !== 'idle'}
                  className="bg-secondary hover:bg-secondary text-white font-semibold py-2 px-4 rounded-md transition duration-300"
                >
                  {idBeingAddedToOrder === largeVariant.id ? (
                    <Loader2 className="text-white animate-spin" />
                  ) : (
                    'Add to Cart'
                  )}
                </button>
              </activeOrderFetcher.Form>
            </div>
          </div>
        )}

        {/* Right Column */}
        <div className="flex flex-col gap-6 lg:col-span-1">
          {/* Medium Item */}
          {mediumVariant && (
            <div className="group relative bg-white rounded-lg shadow-md overflow-hidden p-4 flex flex-col items-center">
              <div className="w-full h-48 overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                <img
                  src={
                    mediumVariant.featuredAsset?.preview ||
                    mediumVariant.product.featuredAsset?.preview
                  }
                  alt={mediumVariant.product.name}
                  className="max-h-full max-w-full object-contain object-center p-2"
                />
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-base font-medium text-gray-900">
                  <Link to={`/products/${mediumVariant.product.slug}`}>
                    {mediumVariant.product.name}
                  </Link>
                </h3>
                <p className="text-lg font-bold text-gray-900 mt-1">
                  {mediumVariant.priceWithTax.toFixed(2)}{' '}
                  {mediumVariant.currencyCode}
                </p>

                <activeOrderFetcher.Form
                  method="post"
                  action="/api/active-order"
                  className="mt-2"
                >
                  <input
                    type="hidden"
                    name="variantId"
                    value={mediumVariant.id}
                  />
                  <input type="hidden" name="action" value="addItemToOrder" />
                  <button
                    type="submit"
                    disabled={activeOrderFetcher.state !== 'idle'}
                    className="text-secondary hover:text-secondary font-semibold text-sm"
                  >
                    {idBeingAddedToOrder === mediumVariant.id ? (
                      <Loader2 className="text-secondary animate-spin" />
                    ) : (
                      'Add to Cart'
                    )}
                  </button>
                </activeOrderFetcher.Form>
              </div>
            </div>
          )}

          {/* Small Items */}
          <div className="grid grid-cols-2 gap-6">
            {smallVariants.slice(0, 2).map((variant) => (
              <div
                key={variant.id}
                className="group relative bg-white rounded-lg shadow-md overflow-hidden p-4 flex flex-col items-center"
              >
                <div className="w-full h-32 overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                  <img
                    src={
                      variant.featuredAsset?.preview ||
                      variant.product.featuredAsset?.preview
                    }
                    alt={variant.product.name}
                    className="max-h-full max-w-full object-contain object-center p-2"
                  />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-sm font-medium text-gray-900">
                    <Link to={`/products/${variant.product.slug}`}>
                      {variant.product.name}
                    </Link>
                  </h3>
                  <p className="text-base font-bold text-gray-900 mt-1">
                    {variant.priceWithTax.toFixed(2)} {variant.currencyCode}
                  </p>

                  <activeOrderFetcher.Form
                    method="post"
                    action="/api/active-order"
                    className="mt-2"
                  >
                    <input type="hidden" name="variantId" value={variant.id} />
                    <input type="hidden" name="action" value="addItemToOrder" />
                    <button
                      type="submit"
                      disabled={activeOrderFetcher.state !== 'idle'}
                      className="text-secondary hover:text-secondary font-semibold text-xs"
                    >
                      {idBeingAddedToOrder === variant.id ? (
                        <Loader2 className="text-secondary animate-spin" />
                      ) : (
                        'Add to Cart'
                      )}
                    </button>
                  </activeOrderFetcher.Form>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
